// src/components/Dashboard/FamilyTimeline.js
import React, { useState, useEffect, useCallback } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { collection, getDocs, getFirestore, query, orderBy, limit } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
  ArrowLeft,
  History,
  Power,
  Home,
  Snowflake,
  LogIn,
  Moon,
  Wifi,
  WifiOff,
  GraduationCap,
  AlertTriangle,
  Zap,
  RefreshCw,
  ChevronDown,
  ChevronRight
} from 'lucide-react';
import IglooSessionTimeline from './IglooSessionTimeline';
import { getGapDisplay } from './timelineUtils';

// Time range options
const TIME_RANGES = {
  '24h': { label: '24h', ms: 24 * 60 * 60 * 1000 },
  '7d': { label: '7 days', ms: 7 * 24 * 60 * 60 * 1000 },
  '30d': { label: '30 days', ms: 30 * 24 * 60 * 60 * 1000 },
  'all': { label: 'All time', ms: null }
};

// Format timestamp to readable format
function formatTimestamp(timestampMs) {
  if (!timestampMs) return '-';
  const date = new Date(timestampMs);
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
}

// Format relative time
function formatRelativeTime(timestampMs) {
  if (!timestampMs) return '-';

  const now = Date.now();
  const diffMs = now - timestampMs;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return `${diffSeconds}s ago`;
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

// Get icon and color for event type
function getEventStyle(eventType, eventData) {
  switch (eventType) {
    case 'boot':
      const bootReason = eventData?.boot_reason || '';
      if (bootReason === 'app_crash') {
        return {
          icon: AlertTriangle,
          color: 'text-red-500',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-300',
          label: 'App Crash (Reboot)'
        };
      }
      if (bootReason === 'power_cycled') {
        return {
          icon: Zap,
          color: 'text-orange-500',
          bgColor: 'bg-orange-100',
          borderColor: 'border-orange-300',
          label: 'Power Cycled'
        };
      }
      if (bootReason === 'power_switched_off') {
        return {
          icon: Power,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-300',
          label: 'Powered On'
        };
      }
      if (bootReason === 'app_restart_from_update') {
        return {
          icon: RefreshCw,
          color: 'text-blue-500',
          bgColor: 'bg-blue-100',
          borderColor: 'border-blue-300',
          label: 'Updated & Rebooted'
        };
      }
      return {
        icon: Power,
        color: 'text-green-500',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-300',
        label: 'Booted'
      };

    case 'signed_in':
      return {
        icon: LogIn,
        color: 'text-green-500',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-300',
        label: 'Signed In'
      };

    case 'entered_home':
      return {
        icon: Home,
        color: 'text-blue-500',
        bgColor: 'bg-blue-100',
        borderColor: 'border-blue-300',
        label: 'Entered Home'
      };

    case 'entered_igloo':
      return {
        icon: Snowflake,
        color: 'text-cyan-500',
        bgColor: 'bg-cyan-100',
        borderColor: 'border-cyan-300',
        label: 'Entered Igloo'
      };

    case 'entered_training':
      return {
        icon: GraduationCap,
        color: 'text-purple-500',
        bgColor: 'bg-purple-100',
        borderColor: 'border-purple-300',
        label: 'Started Training'
      };

    case 'sleep':
      return {
        icon: Moon,
        color: 'text-indigo-500',
        bgColor: 'bg-indigo-100',
        borderColor: 'border-indigo-300',
        label: 'Went to Sleep'
      };

    case 'network_lost':
      return {
        icon: WifiOff,
        color: 'text-red-500',
        bgColor: 'bg-red-100',
        borderColor: 'border-red-300',
        label: 'Network Lost'
      };

    case 'network_restored':
      return {
        icon: Wifi,
        color: 'text-green-500',
        bgColor: 'bg-green-100',
        borderColor: 'border-green-300',
        label: 'Network Restored'
      };

    case 'igloo_exited':
      return {
        icon: Snowflake,
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300',
        label: 'Left Igloo'
      };

    default:
      return {
        icon: History,
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
        borderColor: 'border-gray-300',
        label: eventType
      };
  }
}

// Get additional details for an event
function getEventDetails(eventType, eventData) {
  switch (eventType) {
    case 'boot':
      const details = [];
      if (eventData?.boot_reason) {
        details.push(eventData.boot_reason);
      }
      if (eventData?.last_state) {
        details.push(eventData.last_state);
      }
      if (eventData?.seconds_since_last_ping) {
        details.push(`offline ${formatDuration(eventData.seconds_since_last_ping * 1000)}`);
      }
      return details.join(' · ');

    case 'entered_igloo':
      // Igloo ID shown inline with title, not in details
      return '';

    case 'igloo_exited':
      const exitDetails = [];
      if (eventData?.igloo_id) exitDetails.push(eventData.igloo_id);
      if (eventData?.duration_seconds) {
        exitDetails.push(formatDuration(eventData.duration_seconds * 1000));
      }
      if (eventData?.had_other_participants) {
        exitDetails.push(`${eventData.peak_participant_count || 0} peak`);
      }
      return exitDetails.join(' · ');

    default:
      return '';
  }
}

// Format duration in ms to human readable
function formatDuration(durationMs) {
  if (!durationMs || durationMs < 0) return '-';

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return `${seconds}s`;
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${days}d ${hours % 24}h`;
}

function FamilyTimeline({ familyId, familyName, onBack }) {
  const [allEvents, setAllEvents] = useState([]); // All fetched events
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('7d'); // Default to 7 days
  const [showIglooSession, setShowIglooSession] = useState(false);
  const [selectedIglooSession, setSelectedIglooSession] = useState(null); // { iglooId, timestamp }

  // Fetch events from Firebase
  const fetchEvents = useCallback(async (isRefresh = false) => {
    if (!familyId) return;

    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError(null);

    try {
      const db = getFirestore(app);

      // Fetch events from deviceEvents collection
      const eventsRef = collection(db, 'deviceEvents', familyId, 'events');
      const eventsQuery = query(eventsRef, orderBy('timestamp', 'desc'), limit(500));
      const eventsSnapshot = await getDocs(eventsQuery);

      const eventsList = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by timestamp descending (most recent first)
      eventsList.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));

      setAllEvents(eventsList);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [familyId]);

  // Initial fetch
  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Filter events based on selected time range
  const filteredEvents = allEvents.filter(event => {
    if (timeRange === 'all') return true;
    const cutoff = Date.now() - TIME_RANGES[timeRange].ms;
    return event.timestamp >= cutoff;
  });

  // Check if there are older events outside the current filter
  const hasOlderEvents = timeRange !== 'all' && allEvents.length > filteredEvents.length;

  // Show igloo session view if selected
  if (showIglooSession && selectedIglooSession) {
    return (
      <IglooSessionTimeline
        iglooId={selectedIglooSession.iglooId}
        entryTimestamp={selectedIglooSession.timestamp}
        onBack={() => {
          setShowIglooSession(false);
          setSelectedIglooSession(null);
        }}
      />
    );
  }

  return (
    <Card className="h-full">
      <CardHeader className="py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={onBack} className="h-8 w-8 p-0">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <CardTitle className="flex items-center gap-2 text-lg">
              <History className="w-5 h-5" />
              Timeline: {familyName}
            </CardTitle>
          </div>
          <div className="flex items-center gap-3">
            {/* Time range buttons */}
            <div className="flex items-center gap-1 border rounded-md p-0.5">
              {Object.entries(TIME_RANGES).map(([key, { label }]) => (
                <Button
                  key={key}
                  variant={timeRange === key ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setTimeRange(key)}
                  className="h-7 px-2 text-xs"
                >
                  {label}
                </Button>
              ))}
            </div>

            {/* Event count */}
            <span className="text-sm text-muted-foreground">
              {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''}
            </span>

            {/* Refresh button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => fetchEvents(true)}
              disabled={refreshing}
              className="h-8 w-8 p-0"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            <span>Loading events...</span>
          </div>
        ) : error ? (
          <div className="text-center py-8 text-destructive">
            <AlertTriangle className="w-8 h-8 mx-auto mb-2" />
            <p>Error loading events: {error}</p>
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>No events in {TIME_RANGES[timeRange].label.toLowerCase()}.</p>
            {hasOlderEvents && (
              <p className="mt-2 text-sm">
                There are older events available.{' '}
                <button
                  onClick={() => setTimeRange('all')}
                  className="text-blue-600 hover:underline"
                >
                  View all time
                </button>
              </p>
            )}
          </div>
        ) : (
          <div className="relative">
            {/* Timeline vertical line — positioned at center of icon column */}
            <div className="absolute left-[76px] top-0 bottom-0 w-0.5 bg-border" />

            {/* Events with gap rows */}
            {filteredEvents.map((event, index) => {
              const style = getEventStyle(event.event_type, event);
              const details = getEventDetails(event.event_type, event);
              const Icon = style.icon;
              const prevEvent = index > 0 ? filteredEvents[index - 1] : null;

              // Compute gap from previous event (which is more recent, since list is descending)
              let gap = null;
              if (prevEvent) {
                const gapMs = Math.abs(prevEvent.timestamp - event.timestamp);
                gap = getGapDisplay(gapMs);
              }

              return (
                <React.Fragment key={event.id}>
                  {/* Date divider for 24h+ gaps */}
                  {gap?.showDateDivider && (
                    <div className="flex items-center py-2">
                      <div className="w-14 shrink-0" />
                      <div className="w-10 shrink-0" />
                      <div className="flex-1 flex items-center gap-2 pl-3">
                        <div className="flex-1 h-px bg-border" />
                        <span className="px-2 text-xs text-muted-foreground">
                          {new Date(event.timestamp).toLocaleDateString('en-GB', {
                            weekday: 'short',
                            day: '2-digit',
                            month: 'short'
                          })}
                        </span>
                        <div className="flex-1 h-px bg-border" />
                      </div>
                    </div>
                  )}

                  {/* Gap row: elapsed time on the left of the timeline line */}
                  {gap?.label && (
                    <div className={`flex items-center ${gap.heightClass}`}>
                      <div className="w-14 shrink-0 text-right pr-3">
                        <span className="text-[10px] font-mono text-muted-foreground">{gap.label}</span>
                      </div>
                      <div className="w-10 shrink-0" />
                      <div className="flex-1" />
                    </div>
                  )}

                  {/* Event row */}
                  <div className="flex items-start">
                    <div className="w-14 shrink-0" />
                    <div className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 ${style.bgColor} ${style.borderColor}`}>
                      <Icon className={`w-4 h-4 ${style.color}`} />
                    </div>
                    <div className="flex-1 min-w-0 pl-3 py-1">
                      {/* Line 1: Event name (with inline details for igloo events) */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {event.event_type === 'entered_igloo' && event.igloo_id ? (
                          <>
                            <button
                              onClick={() => {
                                setSelectedIglooSession({
                                  iglooId: event.igloo_id,
                                  timestamp: event.timestamp
                                });
                                setShowIglooSession(true);
                              }}
                              className={`font-medium ${style.color} hover:underline cursor-pointer flex items-center gap-1`}
                              title="View igloo session details"
                            >
                              {style.label}
                              <ChevronRight className="w-3 h-3" />
                            </button>
                            <span className="text-xs text-muted-foreground">{event.igloo_id}</span>
                          </>
                        ) : event.event_type === 'igloo_exited' ? (
                          <>
                            <span className={`font-medium ${style.color}`}>{style.label}</span>
                            {details && (
                              <span className="text-xs text-muted-foreground">{details}</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span className={`font-medium ${style.color}`}>{style.label}</span>
                            {details && (
                              <span className="text-xs text-muted-foreground">{details}</span>
                            )}
                          </>
                        )}
                      </div>
                      {/* Line 2: Date/time and relative time */}
                      <div className="text-xs text-muted-foreground">
                        {formatTimestamp(event.timestamp)} ({formatRelativeTime(event.timestamp)})
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })}

            {/* Indicator for more events */}
            {hasOlderEvents && (
              <div className="mt-6 flex items-center justify-center ml-14">
                <button
                  onClick={() => setTimeRange('all')}
                  className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-dashed rounded-lg hover:border-solid transition-all"
                >
                  <ChevronDown className="w-4 h-4" />
                  <span>{allEvents.length - filteredEvents.length} older event{allEvents.length - filteredEvents.length !== 1 ? 's' : ''} available</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default FamilyTimeline;
